/**
 * RAG source display names and fault-code reference links.
 * Makes "Knowledge base used" show friendly names (e.g. "Cummins QSL9 Service Manual")
 * and adds external fault-code links (Autocodes, Engine-codes).
 */

/** Display names for KB documents (filename → human-readable title) */
export const RAG_SOURCE_DISPLAY_NAMES = {
  'repair_procedures.md': 'Cummins QSL9 / Repair Procedures Manual',
  'osha_1910_147_loto_standard.md': 'OSHA 1910.147 LOTO Standard',
  'hdkag_generator_troubleshooting.md': 'HDKAG Generator Troubleshooting',
  'qsol_service_navigation_guide.md': 'QSOL Service Navigation Guide',
  'loto_checklist_template.md': 'LOTO Checklist Template',
  'data_collection_templates.md': 'Data Collection Templates',
  'source_urls_and_notes.md': 'Source URLs & Notes',
  'troubleshooting_trees.json': 'Troubleshooting Trees',
  'warranty_decision_tree.md': 'Warranty Decision Tree',
  'escalation_rules_and_risk_matrix.md': 'Escalation Rules & Risk Matrix',
  'symptom_to_fault_code_mapping.json': 'Symptom to Fault Code Mapping',
};

/**
 * Get display name for a RAG source (filename). Falls back to filename if not in registry.
 * @param {string} filename - e.g. "repair_procedures.md"
 * @returns {string}
 */
export function getSourceDisplayName(filename) {
  const name = typeof filename === 'string' ? filename.trim() : String(filename);
  return RAG_SOURCE_DISPLAY_NAMES[name] || name;
}

/**
 * External reference links for a fault code.
 * Uses Autocodes which has pages for OBD codes at https://www.autocodes.com/{code}.html
 * @param {string} faultCode - e.g. "P0563", "P0217", "P0340"
 * @returns {{ name: string, url: string }[]}
 */
export function getFaultCodeReferenceLinks(faultCode) {
  if (!faultCode || typeof faultCode !== 'string') return [];
  const code = faultCode.trim().toLowerCase();
  if (!code) return [];
  return [
    {
      name: `${faultCode.toUpperCase()} on Autocodes`,
      url: `https://www.autocodes.com/${code}.html`,
    },
  ];
}

/**
 * Base URL for API (same origin in dev; set REACT_APP_API_BASE in prod if API is on another host).
 * Ensures "View document" links hit the backend and work when opened in a new tab.
 */
function getApiBase() {
  if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_BASE) {
    return process.env.REACT_APP_API_BASE.replace(/\/$/, '');
  }
  return '';
}

/**
 * Resolve link for a RAG source. Uses /api/kb/{filename}; with REACT_APP_API_BASE for cross-origin deployment.
 * @param {string} filename
 * @returns {string} URL to open the source
 */
export function getSourceUrl(filename) {
  const name = typeof filename === 'string' ? filename.trim() : String(filename);
  const base = getApiBase();
  return `${base}/api/kb/${encodeURIComponent(name)}`;
}
